<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/seo-score/partials/modals/ui/ModalHeading.vue";
import ModalText from "@/components/seo-score/partials/modals/ui/ModalText.vue";
import {isOnLocalDomain, kitName} from "@/composables/useLocalDomains";
import useWebsite from "@/composables/useWebsite";
import {openIntercom} from "@/composables/useIntercom";
import {computed} from "vue";

const emit = defineEmits(['close']);

const props = defineProps({
  visibilityScore: {
    type: Number,
    required: true,
  },
});

const {website} = useWebsite();

const connectedAccountsSettingsUrl = computed(
  () => `/website/settings/${website.value?.id}#connected_accounts`,
);

const handleSpeakToSpecialist = () => {
  window.mixpanel('SEO Score: Speak to Specialist', { visibilityScore: props.visibilityScore });
  emit('close');
  openIntercom();
};
</script>

<template>
  <Teleport to=".tailwind">
    <BaseModal
      modal-class="tw-w-[800px]"
      @close="emit('close')"
    >
      <ModalContentWrapper class="!tw-gap-6">
        <ModalHeading class="tw-text-3xl">
          How to improve your Visibility Score
        </ModalHeading>

        <ModalText>
          The fastest way to improve your score is to give {{ kitName }} more information to work with.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>1. Publish the pages and blogs {{ kitName }} creates</ModalHeading>

        <ModalText>
          Every new page gives Google and AI more reasons to find your business. Fresh content helps you appear for more searches and gives customers more ways to discover you.
        </ModalText>

        <ModalText>
          <strong>Action: </strong>
          <a
            href="/approval-center"
            class="tw-text-info hover:tw-underline"
          >Review</a>
          and 
          <a
            href="/changelog"
            class="tw-text-info hover:tw-underline"
          >publish</a> your suggested pages and blog posts.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>2. Focus on the right keywords</ModalHeading>

        <ModalText>
          Choose the services and locations that matter most to your business. {{ kitName }} will prioritise the searches most likely to generate enquiries.
        </ModalText>

        <ModalText>
          <strong>Action: </strong>
          <a
            href="/keywords"
            class="tw-text-info hover:tw-underline"
          >Review</a>
          your tracked keywords.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>3. Match each keyword to the right page</ModalHeading>

        <ModalText>
          Help Google understand which page should rank for each service.
        </ModalText>

        <ModalText>
          <strong>Action: </strong>
          <a
            href="/sitemap"
            class="tw-text-info hover:tw-underline"
          >Check</a>
          that each important keyword has a relevant page.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>4. Approve your website improvements</ModalHeading>

        <ModalText>
          {{ kitName }} regularly suggests updates to improve your visibility.
        </ModalText>

        <ModalText>
          <strong>Action: </strong>
          <a
            href="/approval-center"
            class="tw-text-info hover:tw-underline"
          >Review</a>
          and approve any pending website updates.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>5. Connect your Google accounts</ModalHeading>

        <ModalText>
          Connecting
          <a
            :href="connectedAccountsSettingsUrl"
            class="tw-text-info hover:tw-underline"
          >Search Console</a>
          and
          <a
            :href="connectedAccountsSettingsUrl"
            class="tw-text-info hover:tw-underline"
          >Google Analytics</a>
          gives {{ kitName }} better data to spot opportunities and measure progress.
        </ModalText>

        <ModalText>
          <strong>Action:</strong> Connect your Google accounts.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>6. Build trust across the web</ModalHeading>

        <ModalText>
          Google and AI trust businesses that are mentioned consistently online.
        </ModalText>

        <ModalText>
          <strong>Action:</strong> Add your business to relevant directories, industry websites and local organisations.
        </ModalText>

        <hr class="tw-my-2 tw-border-neutral-200">

        <ModalHeading>7. Keep your Google Business Profile active</ModalHeading>

        <ModalText>
          Add fresh photos and reply to reviews regularly.
        </ModalText>

        <ModalText>
          <strong>Action:</strong> Show Google and AI that your business is active, trustworthy and worth recommending.
        </ModalText>

        <template v-if="isOnLocalDomain">
          <hr class="tw-my-2 tw-border-neutral-200">

          <ModalHeading>Need a hand?</ModalHeading>

          <ModalHeading>Let {{ kitName }} do the thinking. Let us do the doing.</ModalHeading>

          <ModalText>
            If you'd rather not spend time managing SEO, our specialists can take care of everything for you.
          </ModalText>

          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-gap-1 tw-text-info tw-font-semibold hover:tw-underline"
            @click="handleSpeakToSpecialist"
          >
            Speak to an SEO specialist
          </button>
        </template>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
